import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listExamConfigs } from "../../api/examApi";
import { saveExamRoutineGenerate, listExamRoutineGenerate, deleteExamRoutineGenerate } from "../../api/examRoutineGenerateApi";
import "../Attendance/Attendance.css";
import "./ClassRoutine.css";

const academicYearApi = createLookupApi("academic-years");
const classApi = createLookupApi("classes");
const groupApi = createLookupApi("groups");
const subjectApi = createLookupApi("subjects");

const URL_TO_TAB = {
  "exam-routine-create": "create",
  "exam-routine-create-list": "list",
};

const TABS = [
  { key: "create", label: "Routine Create" },
  { key: "list", label: "Routine Create List" },
];

/**
 * Direct port of digicampusbd's `exam-routine-create` / `exam-routine-create-list`
 * (one page, two tabs in the old app itself — `ExamRoutineController`'s
 * `/create` + `/generate/list` + `/delete/2`). This is the real
 * `ExamRoutineGenerate` data ({@code exam_routine_generate}, 8,093 real rows
 * — one of the most heavily-used tables found in this migration) that the
 * still-deferred `admit-card-with-exam-routine` needs; building this module
 * is what unblocks that url (see MIGRATION.md).
 *
 * Same documented simplification as `ClassRoutineBuilderPage`: the subject
 * picker uses the plain `subjects` lookup rather than the old app's
 * class+group-filtered `SubjectConfiguration` list.
 */
export default function ExamRoutineGeneratePage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "create");

  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "create");
  }, [pageUrl]);

  useEffect(() => {
    academicYearApi.list().then(setAcademicYears);
    classApi.list().then(setClasses);
    groupApi.list().then(setGroups);
    subjectApi.list().then(setSubjects);
  }, []);

  // ---------------- Create tab ----------------
  const [academicYear, setAcademicYear] = useState("");
  const [classId, setClassId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examId, setExamId] = useState("");
  const [groupIds, setGroupIds] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [examDate, setExamDate] = useState("");
  const [examTime, setExamTime] = useState("");
  const [roomNo, setRoomNo] = useState("");
  const [examSession, setExamSession] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (academicYears.length > 0 && !academicYear) setAcademicYear(academicYears[0].name);
    if (classes.length > 0 && !classId) setClassId(String(classes[0].id));
  }, [academicYears, classes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!classId) return;
    listExamConfigs(classId).then((configs) => {
      setExamConfigs(configs);
      setExamId(configs.length > 0 ? String(configs[0].examId) : "");
    });
  }, [classId]);

  const toggleGroup = (id) => {
    setGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    if (!examId) return setError("Please select an exam.");
    if (groupIds.length === 0) return setError("Please select at least one group.");
    if (!subjectId) return setError("Please select a subject.");
    if (!examDate) return setError("Please select an exam date.");

    setSaving(true);
    try {
      await saveExamRoutineGenerate({
        academicYear,
        classId,
        examId,
        subjectId,
        groupIds,
        examDate,
        examTime,
        roomNo,
        examSession,
      });
      setSuccess("Exam routine successfully save");
      setSubjectId("");
      setRoomNo("");
      setExamDate("");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save exam routine.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- List tab ----------------
  const [listAcademicYear, setListAcademicYear] = useState("");
  const [listClassId, setListClassId] = useState("");
  const [listExamConfigsState, setListExamConfigsState] = useState([]);
  const [listExamId, setListExamId] = useState("");
  const [listGroupIds, setListGroupIds] = useState([]);
  const [rows, setRows] = useState(null);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (academicYears.length > 0 && !listAcademicYear) setListAcademicYear(academicYears[0].name);
    if (classes.length > 0 && !listClassId) setListClassId(String(classes[0].id));
  }, [academicYears, classes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!listClassId) return;
    listExamConfigs(listClassId).then((configs) => {
      setListExamConfigsState(configs);
      setListExamId(configs.length > 0 ? String(configs[0].examId) : "");
    });
  }, [listClassId]);

  const toggleListGroup = (id) => {
    setListGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));
  };

  const handleListSearch = async () => {
    setError("");
    setRows(null);
    if (!listExamId || listGroupIds.length === 0) {
      setError("Please select class, exam and at least one group.");
      return;
    }
    setListLoading(true);
    try {
      const data = await listExamRoutineGenerate(listAcademicYear, listExamId, listClassId, listGroupIds);
      if (data.length === 0) {
        setError("Routine has not yet been created!");
      } else {
        setRows(data);
      }
    } catch (err) {
      setError("Failed to load exam routine.");
    } finally {
      setListLoading(false);
    }
  };

  const handleDelete = async (examRoutineId) => {
    setError("");
    setSuccess("");
    try {
      await deleteExamRoutineGenerate(examRoutineId);
      setSuccess("Exam routine successfully deleted.");
      handleListSearch();
    } catch (err) {
      setError("Failed to delete exam routine.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Exam Routine</h1>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      {tab === "create" && (
        <div className="user-form" style={{ marginTop: 16 }}>
          <div className="attendance-filters">
            <label>
              Academic Year
              <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Class
              <select value={classId} onChange={(e) => setClassId(e.target.value)}>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Exam
              <select value={examId} onChange={(e) => setExamId(e.target.value)}>
                {examConfigs.length === 0 && <option value="">No exams configured</option>}
                {examConfigs.map((ec) => (
                  <option key={ec.examId} value={ec.examId}>
                    {ec.examName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Subject
              <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
                <option value="">Select Subject</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="attendance-filters">
            <label>
              Exam Date
              <input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} />
            </label>
            <label>
              Exam Time
              <input type="text" placeholder="e.g. 10:00 AM-12:00 PM" value={examTime} onChange={(e) => setExamTime(e.target.value)} />
            </label>
            <label>
              Room No.
              <input type="text" value={roomNo} onChange={(e) => setRoomNo(e.target.value)} />
            </label>
            <label>
              Routine Session
              <input type="text" value={examSession} onChange={(e) => setExamSession(e.target.value)} />
            </label>
          </div>
          <div className="form-row">
            <span className="admit-card-label">Select Group:</span>{" "}
            {groups.map((g) => (
              <label key={g.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
                <input type="checkbox" checked={groupIds.includes(String(g.id))} onChange={() => toggleGroup(String(g.id))} />
                {g.name}
              </label>
            ))}
          </div>
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {tab === "list" && (
        <div>
          <div className="attendance-filters">
            <label>
              Academic Year
              <select value={listAcademicYear} onChange={(e) => setListAcademicYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Class
              <select value={listClassId} onChange={(e) => setListClassId(e.target.value)}>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Exam
              <select value={listExamId} onChange={(e) => setListExamId(e.target.value)}>
                {listExamConfigsState.length === 0 && <option value="">No exams configured</option>}
                {listExamConfigsState.map((ec) => (
                  <option key={ec.examId} value={ec.examId}>
                    {ec.examName}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <span className="admit-card-label">Select Group:</span>{" "}
            {groups.map((g) => (
              <label key={g.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
                <input type="checkbox" checked={listGroupIds.includes(String(g.id))} onChange={() => toggleListGroup(String(g.id))} />
                {g.name}
              </label>
            ))}
            <button type="button" className="btn-primary" onClick={handleListSearch} style={{ marginLeft: 12 }}>
              Search
            </button>
          </div>

          {listLoading ? (
            <p>Loading...</p>
          ) : (
            rows && (
              <div className="table-scroll">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Academic Year</th>
                      <th>Session</th>
                      <th>Class Name</th>
                      <th>Group Name</th>
                      <th>Exam Name</th>
                      <th>Exam Date</th>
                      <th>Exam Time</th>
                      <th>Room No</th>
                      <th>Subject Name</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.examRoutineId}>
                        <td>{r.academicYear}</td>
                        <td>{r.examSession}</td>
                        <td>{r.className}</td>
                        <td>{r.groupName}</td>
                        <td>{r.examName}</td>
                        <td>{r.examDate}</td>
                        <td>{r.examTime}</td>
                        <td>{r.roomNo}</td>
                        <td>{r.subjectName}</td>
                        <td>
                          <button type="button" className="btn-danger" onClick={() => handleDelete(r.examRoutineId)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </MainLayout>
  );
}
